import React from 'react';
import { useTranslation } from 'react-i18next';
import LegalPage from './LegalPage';

function Privacy() {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const content = isEnglish ? (
    <>
      <h2>1. Introduction</h2>
      <p>Welcome to Alhamdulillah. We are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application.</p>

      <h2>2. Information We Collect</h2>
      <h3>2.1 Personal Information</h3>
      <ul>
        <li>Email address (for account creation and authentication)</li>
        <li>Name or username (optional)</li>
        <li>Profile information you choose to provide</li>
        <li>Language preference</li>
      </ul>

      <h3>2.2 User-Generated Content</h3>
      <ul>
        <li>Voice recordings of your gratitude moments</li>
        <li>Written journal entries</li>
        <li>Emotional tracking data (mood, gratitude level, inner peace ratings)</li>
        <li>Reminder preferences and settings</li>
      </ul>

      <h3>2.3 Device Information</h3>
      <ul>
        <li>Device type and model</li>
        <li>Operating system and version</li>
        <li>Unique device identifiers</li>
        <li>App version</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <ul>
        <li>Create and manage your account</li>
        <li>Store and sync your personal content across devices</li>
        <li>Display your gratitude journal and emotional tracking history</li>
        <li>Send reminders based on your preferences</li>
        <li>Improve App features and performance</li>
        <li>Provide customer support</li>
      </ul>

      <h2>4. Data Storage and Security</h2>
      <p>Your personal content is securely stored using industry-standard cloud storage services. We use encryption both in transit and at rest to protect your data. We implement appropriate technical and organizational measures including end-to-end encryption, secure authentication protocols, and regular security audits.</p>

      <h2>5. Data Sharing and Disclosure</h2>
      <p><strong>We Do Not Sell Your Data.</strong> We do not sell, rent, or trade your personal information to third parties for their marketing purposes.</p>
      <p>We may share your information with trusted third-party service providers who assist us in operating the App (cloud hosting, analytics, customer support). These providers are bound by confidentiality agreements.</p>

      <h2>6. Your Rights and Choices</h2>
      <ul>
        <li>Access your personal information</li>
        <li>Update or correct your data</li>
        <li>Export your data</li>
        <li>Delete your account and data</li>
        <li>Withdraw consent for data processing</li>
      </ul>

      <h2>7. Children's Privacy</h2>
      <p>Alhamdulillah is designed for users of all ages. We do not knowingly collect personal information from children under 13 without parental consent.</p>

      <h2>8. International Data Transfers</h2>
      <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data.</p>

      <h2>9. Changes to This Privacy Policy</h2>
      <p>We may update this Privacy Policy from time to time. We will notify you of any material changes through the App or via email.</p>

      <h2>10. Contact Us</h2>
      <p>If you have questions or concerns regarding this Privacy Policy, please contact us through the support section within the App or our website.</p>
    </>
  ) : (
    <>
      <h2>1. Introduction</h2>
      <p>Bienvenue sur Alhamdulillah. Nous nous engageons à protéger votre vie privée et vos informations personnelles. Cette Politique de Confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations lorsque vous utilisez notre application mobile.</p>

      <h2>2. Informations Que Nous Collectons</h2>
      <h3>2.1 Informations Personnelles</h3>
      <ul>
        <li>Adresse e-mail (pour la création et l'authentification du compte)</li>
        <li>Nom ou nom d'utilisateur (facultatif)</li>
        <li>Informations de profil que vous choisissez de fournir</li>
        <li>Préférence linguistique</li>
      </ul>

      <h3>2.2 Contenu Généré par l'Utilisateur</h3>
      <ul>
        <li>Enregistrements vocaux de vos moments de gratitude</li>
        <li>Entrées de journal écrites</li>
        <li>Données de suivi émotionnel (humeur, niveau de gratitude, évaluations de paix intérieure)</li>
        <li>Préférences et paramètres de rappel</li>
      </ul>

      <h3>2.3 Informations sur l'Appareil</h3>
      <ul>
        <li>Type et modèle d'appareil</li>
        <li>Système d'exploitation et version</li>
        <li>Identifiants uniques de l'appareil</li>
        <li>Version de l'application</li>
      </ul>

      <h2>3. Comment Nous Utilisons Vos Informations</h2>
      <ul>
        <li>Créer et gérer votre compte</li>
        <li>Stocker et synchroniser votre contenu personnel sur plusieurs appareils</li>
        <li>Afficher votre journal de gratitude et l'historique de suivi émotionnel</li>
        <li>Envoyer des rappels en fonction de vos préférences</li>
        <li>Améliorer les fonctionnalités et les performances de l'Application</li>
        <li>Fournir un support client</li>
      </ul>

      <h2>4. Stockage et Sécurité des Données</h2>
      <p>Votre contenu personnel est stocké en toute sécurité en utilisant des services de stockage cloud conformes aux normes de l'industrie. Nous utilisons le chiffrement en transit et au repos pour protéger vos données. Nous mettons en œuvre des mesures techniques et organisationnelles appropriées incluant le chiffrement de bout en bout, des protocoles d'authentification sécurisés et des audits de sécurité réguliers.</p>

      <h2>5. Partage et Divulgation des Données</h2>
      <p><strong>Nous Ne Vendons Pas Vos Données.</strong> Nous ne vendons, ne louons ni n'échangeons vos informations personnelles à des tiers à des fins de marketing.</p>
      <p>Nous pouvons partager vos informations avec des fournisseurs de services tiers de confiance qui nous aident à exploiter l'Application (hébergement cloud, analyses, support client). Ces fournisseurs sont liés par des accords de confidentialité.</p>

      <h2>6. Vos Droits et Choix</h2>
      <ul>
        <li>Accéder à vos informations personnelles</li>
        <li>Mettre à jour ou corriger vos données</li>
        <li>Exporter vos données</li>
        <li>Supprimer votre compte et vos données</li>
        <li>Retirer votre consentement au traitement des données</li>
      </ul>

      <h2>7. Confidentialité des Enfants</h2>
      <p>Alhamdulillah est conçu pour les utilisateurs de tous âges. Nous ne collectons pas sciemment d'informations personnelles auprès d'enfants de moins de 13 ans sans le consentement parental.</p>

      <h2>8. Transferts Internationaux de Données</h2>
      <p>Vos informations peuvent être transférées et traitées dans des pays autres que votre pays de résidence. Nous veillons à ce que des garanties appropriées soient en place pour protéger vos données.</p>

      <h2>9. Modifications de Cette Politique de Confidentialité</h2>
      <p>Nous pouvons mettre à jour cette Politique de Confidentialité de temps en temps. Nous vous informerons de tout changement important via l'Application ou par e-mail.</p>

      <h2>10. Nous Contacter</h2>
      <p>Si vous avez des questions ou préoccupations concernant cette Politique de Confidentialité, veuillez nous contacter via la section support dans l'Application ou notre site web.</p>
    </>
  );

  return (
    <LegalPage
      title={isEnglish ? 'Privacy Policy' : 'Politique de Confidentialité'}
      content={content}
    />
  );
}

export default Privacy;
